/**
 * SPDX-FileCopyrightText: 2026 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

/**
 * Toasts, fetched when something actually has to be said.
 *
 * `@nextcloud/dialogs` re-exports its whole surface — the file picker, the
 * conflict picker, the dialog builder, the icon set they use — from a single
 * module, so importing `showError` from it cannot be tree-shaken down to the
 * toast: it puts **259 KB** into the bundle, 18% of this app's main entry, to
 * say "Could not load the timeline".
 *
 * Nothing waits for a toast, so nothing here needs to be awaited: each call
 * returns a promise that the caller is free to ignore, and the library arrives
 * in its own chunk the first time one is shown. The import is memoised by the
 * module system, so the second toast is immediate.
 *
 * The signatures are `@nextcloud/dialogs`' own, forwarded unchanged, except
 * that `showError` also takes a sentence with the server's own words beside it.
 */

import { t } from '@nextcloud/l10n'

/**
 * The stylesheet travels with the library, in the same chunk.
 *
 * `@nextcloud/dialogs` 7 builds a toast out of CSS-module class names --
 * `_toastContainer_1biev_1`, `_toast_v43ag_11` -- and ships the rules for them
 * in a stylesheet of its own. The server's `core/css/toast.css` styles
 * `.toastify.toast`, which is the markup an older major produced, and matches
 * none of them; no other stylesheet this app loads defines them either. Without
 * this import a toast is a block with no position, no background and no
 * shadow, which the page then lays out where any unstyled block goes: the top
 * left corner.
 *
 * Imported here rather than at the top of the module so it lands in the `toast`
 * chunk with the code that needs it, instead of in the entry this file was
 * split out of to keep small.
 *
 * @return {Promise<typeof import('@nextcloud/dialogs')>} the dialogs module, loaded once
 */
function dialogs() {
	return Promise.all([
		import(/* webpackChunkName: "toast" */ '@nextcloud/dialogs'),
		import(/* webpackChunkName: "toast" */ '@nextcloud/dialogs/style.css'),
	]).then(([module]) => module)
}

/**
 * The sentence, and the server's own words under a "Details" the person can
 * open. Built from text nodes, so nothing the server said becomes markup.
 *
 * @param {{text: string, details: string}} error as utils/serverError.js makes it
 * @return {HTMLElement} the toast's content
 */
function withDetails(error) {
	const content = document.createElement('div')
	content.append(error.text)

	const details = document.createElement('details')
	const summary = document.createElement('summary')
	summary.textContent = t('social', 'Details')
	const said = document.createElement('code')
	said.textContent = error.details
	said.style.display = 'block'
	said.style.whiteSpace = 'pre-wrap'
	said.style.overflowWrap = 'anywhere'
	details.append(summary, said)
	content.append(details)

	return content
}

/**
 * @param {string|{text: string, details: string}} text what went wrong, or that
 *        and what the server said, as utils/serverError.js makes it; the
 *        server's words go behind "Details"
 * @param {object} [options] as `@nextcloud/dialogs` takes them
 * @return {Promise<object>} the toast, for a caller that wants to hide it
 */
export async function showError(text, options) {
	const toasts = await dialogs()
	if (typeof text === 'string') {
		return toasts.showError(text, options)
	}
	if (!text.details || text.details === text.text) {
		return toasts.showError(text.text, options)
	}

	return toasts.showMessage(withDetails(text), { ...options, type: toasts.ToastType.ERROR })
}

/**
 * @param {string} text what worked
 * @param {object} [options] as `@nextcloud/dialogs` takes them
 * @return {Promise<object>} the toast
 */
export async function showSuccess(text, options) {
	return (await dialogs()).showSuccess(text, options)
}

/**
 * @param {string} text what the reader should know about
 * @param {object} [options] as `@nextcloud/dialogs` takes them
 * @return {Promise<object>} the toast
 */
export async function showWarning(text, options) {
	return (await dialogs()).showWarning(text, options)
}

/**
 * @param {string} text what happened
 * @param {object} [options] as `@nextcloud/dialogs` takes them
 * @return {Promise<object>} the toast
 */
export async function showInfo(text, options) {
	return (await dialogs()).showInfo(text, options)
}

/**
 * A toast with an Undo button, which stays for the ten seconds
 * `@nextcloud/dialogs` gives an undo.
 *
 * @param {string} text what was just done
 * @param {(event: MouseEvent) => void} onUndo called when Undo is pressed
 * @param {object} [options] as `@nextcloud/dialogs` takes them
 * @return {Promise<object>} the toast
 */
export async function showUndo(text, onUndo, options) {
	return (await dialogs()).showUndo(text, onUndo, options)
}
